import { useState } from "react";
import { Loader2, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";
import { SERVICES } from "@/lib/site";

export default function EstimateForm({
  source = "contact_form",
  defaultService = "",
  compact = false,
  variant = "light",
}) {
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    service_needed: defaultService || "Other",
    property_address: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email) {
      toast.error("Name, phone and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({ ...form, source });
      setDone(true);
      toast.success("Thanks! We'll be in touch within one business day.");
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="estimate-form-success"
        className={`p-8 rounded-md ${variant === "dark" ? "bg-white/5 text-brand-bone" : "bg-brand-limestone text-brand-ink"}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-brand-obsidian flex items-center justify-center">
            <Check className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <p className="font-heading text-xl font-bold">Request received.</p>
            <p className="body-sm">A specialist will reach out within one business day.</p>
          </div>
        </div>
      </div>
    );
  }

  const labelCls = variant === "dark" ? "text-sm font-semibold text-brand-bone mb-2 block" : "label-base";
  const inputCls = variant === "dark"
    ? "w-full bg-white/5 border border-white/15 rounded-sm px-4 py-3 text-base text-brand-bone placeholder:text-brand-bone/50 focus:outline-none focus:ring-2 focus:ring-brand-bone/60 focus:border-transparent transition"
    : "input-base";

  return (
    <form
      data-testid="estimate-form"
      onSubmit={handleSubmit}
      className={`grid grid-cols-1 ${compact ? "" : "md:grid-cols-2"} gap-4`}
    >
      <div className={compact ? "" : ""}>
        <label className={labelCls}>Full Name</label>
        <input
          data-testid="estimate-name"
          required
          className={inputCls}
          value={form.full_name}
          onChange={onChange("full_name")}
          placeholder="Your name"
        />
      </div>
      <div>
        <label className={labelCls}>Phone</label>
        <input
          data-testid="estimate-phone"
          required
          className={inputCls}
          value={form.phone}
          onChange={onChange("phone")}
          placeholder="(818) 606-3769"
        />
      </div>
      <div className={compact ? "" : "md:col-span-2"}>
        <label className={labelCls}>Email</label>
        <input
          data-testid="estimate-email"
          required
          type="email"
          className={inputCls}
          value={form.email}
          onChange={onChange("email")}
          placeholder="you@email.com"
        />
      </div>
      <div>
        <label className={labelCls}>Service Needed</label>
        <select
          data-testid="estimate-service"
          className={inputCls}
          value={form.service_needed}
          onChange={onChange("service_needed")}
        >
          <option value="Other">Choose service…</option>
          {SERVICES.map((s) => (
            <option key={s.slug} value={s.title}>{s.title}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelCls}>Property Address</label>
        <input
          data-testid="estimate-address"
          className={inputCls}
          value={form.property_address}
          onChange={onChange("property_address")}
          placeholder="Street, City"
        />
      </div>
      <div className="md:col-span-2">
        <label className={labelCls}>Message</label>
        <textarea
          data-testid="estimate-message"
          rows={4}
          className={`${inputCls} resize-none`}
          value={form.message}
          onChange={onChange("message")}
          placeholder="Tell us about your project…"
        />
      </div>
      <div className="md:col-span-2">
        <button
          data-testid="estimate-submit"
          type="submit"
          disabled={submitting}
          className={`btn-primary w-full sm:w-auto ${variant === "dark" ? "bg-brand-bone text-brand-ink hover:bg-white" : ""}`}
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          Request Free Estimate
        </button>
        <p className="mt-3 text-xs text-brand-ash">
          By submitting you agree to be contacted by ME Builders. No spam — ever.
        </p>
      </div>
    </form>
  );
}
